import React, { useState, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import {
  getLostFoundItems,
  deleteLostFound,
  getVisibleOptionalFields,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import {
  LOST_FOUND_REWARD_FIELD_KEY,
  LOST_FOUND_CATEGORY_FIELD_KEY,
} from "../section-visibility/optionalFieldConstants";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import {
  EnhancedPageLayout,
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { cn } from "../../lib/utils";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  DollarSign,
  Tag,
  Phone,
  MapPin,
  Calendar,
  Package,
  Grid3X3,
  List,
  Table,
} from "lucide-react";

export default function LostFoundPage() {
  useRequireSectionVisible("showLostFound");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const deleteLostFoundFn = useAction(deleteLostFound);

  // State for filters and view mode
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "title">("newest");

  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("table");

  // Fetch items
  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery(getLostFoundItems, {
    locationId: selectedLocation?.id || "",
    filters: {
      type:
        selectedType === "all"
          ? undefined
          : (selectedType as "LOST_ITEM" | "FOUND_ITEM"),
    },
  });

  const { data: visibleOptionalFields } = useQuery(getVisibleOptionalFields);
  const optionalByKey =
    (visibleOptionalFields as { byKey?: Record<string, boolean> } | undefined)
      ?.byKey ?? {};
  const showRewardField = optionalByKey[LOST_FOUND_REWARD_FIELD_KEY] !== false;
  const showCategoryField =
    optionalByKey[LOST_FOUND_CATEGORY_FIELD_KEY] !== false;

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = items as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item: any) =>
          item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.metadata?.locationFound
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    // Category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (item: any) => item.metadata?.category === selectedCategory,
      );
    }

    // Sort
    switch (sortBy) {
      case "newest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "oldest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "title":
        filtered = [...filtered].sort((a: any, b: any) =>
          a.title.localeCompare(b.title),
        );
        break;
    }

    return filtered;
  }, [items, searchTerm, selectedType, selectedCategory, sortBy]);

  const handleDelete = async (itemId: string) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await deleteLostFoundFn({ itemId });
      } catch (error) {
        console.error("Error deleting item:", error);
        alert("Error deleting item. Please try again.");
      }
    }
  };

  const getTypeColor = (type: string) => {
    return type === "LOST_ITEM"
      ? "bg-destructive/10 text-destructive border border-destructive/20"
      : "bg-success/10 text-success border border-success/20";
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      ELECTRONICS: "bg-primary/10 text-primary border border-primary/20",
      CLOTHING: "bg-accent/10 text-accent border border-accent/20",
      ACCESSORIES: "bg-secondary/10 text-secondary border border-secondary/20",
      DOCUMENTS: "bg-warning/10 text-warning border border-warning/20",
      KEYS: "bg-muted text-muted-foreground border border-border",
      BOOKS: "bg-secondary/10 text-secondary border border-secondary/20",
      SPORTS: "bg-success/10 text-success border border-success/20",
      OTHER: "bg-primary/10 text-primary border border-primary/20",
    };
    return (
      colors[category as keyof typeof colors] ||
      "bg-muted text-muted-foreground border border-border"
    );
  };

  const headerContent = (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Lost & Found</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Report and find lost items at {selectedLocation?.name || "Location"} (
            {filteredItems.length} items)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EnhancedActionButton
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            icon={Filter}
            label=""
            variant={isFilterExpanded ? "primary" : "secondary"}
            className="hidden lg:flex"
          />

          {/* Mobile Filter Button */}
          <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
            <EnhancedActionButton
              onClick={() => setIsFilterExpanded(true)}
              icon={Filter}
              label=""
              variant={isFilterExpanded ? "primary" : "secondary"}
              className="lg:hidden"
            />
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="py-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="LOST_ITEM">Lost Items</option>
                    <option value="FOUND_ITEM">Found Items</option>
                  </select>
                </div>

                {showCategoryField && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Category</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Categories</option>
                      <option value="ELECTRONICS">Electronics</option>
                      <option value="CLOTHING">Clothing</option>
                      <option value="ACCESSORIES">Accessories</option>
                      <option value="DOCUMENTS">Documents</option>
                      <option value="KEYS">Keys</option>
                      <option value="BOOKS">Books</option>
                      <option value="SPORTS">Sports</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="title">Title A-Z</option>
                  </select>
                </div>

                <Button 
                  className="w-full" 
                  onClick={() => setIsFilterExpanded(false)}
                >
                  Apply Filters
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          {user?.isAdmin && (
            <EnhancedActionButton
              onClick={() => {
                if (selectedLocation?.locationCode) {
                  navigate(
                    `/locations/${selectedLocation.locationCode}/lost-found/add`,
                  );
                } else {
                  navigate("/lost-found/add");
                }
              }}
              icon={Plus}
              label={<span className="hidden sm:inline">Report Item</span>}
            />
          )}
        </div>
      </div>
    </div>
  );

  const filtersContent = (
    <div 
      className={cn(
        "bg-sidebar-hover border-t border-border transition-all duration-300 overflow-hidden hidden lg:block",
        isFilterExpanded ? "max-h-[500px]" : "max-h-0 border-none"
      )}
    >
      <div className="px-6 py-3 space-y-3">
        {/* Column Headers - Only for table view */}
        {viewMode === "table" && (
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
            <div className={cn("min-w-0 truncate", showCategoryField ? "col-span-3" : "col-span-4")}>Item</div>
            <div className="col-span-1 min-w-0 truncate">Type</div>
            {showCategoryField && <div className="col-span-2 min-w-0 truncate">Category</div>}
            <div className="col-span-2 min-w-0 truncate">Location</div>
            <div className="col-span-1 min-w-0 truncate">Reward</div>
            <div className={user?.isAdmin ? "col-span-1 min-w-0 truncate" : "col-span-2 min-w-0 truncate"}>Author</div>
            {user?.isAdmin && <div className="col-span-2 text-right min-w-0 truncate">Actions</div>}
          </div>
        )}
        
        {/* Column Filters */}
        <div className="grid grid-cols-12 gap-4">
          <div className={cn(showCategoryField ? "col-span-3" : "col-span-4")}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="col-span-1">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All</option>
              <option value="LOST_ITEM">Lost</option>
              <option value="FOUND_ITEM">Found</option>
            </select>
          </div>
          {showCategoryField && (
            <div className="col-span-2">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Category</option>
                <option value="ELECTRONICS">Electronics</option>
                <option value="CLOTHING">Clothing</option>
                <option value="ACCESSORIES">Accessories</option>
                <option value="DOCUMENTS">Documents</option>
                <option value="KEYS">Keys</option>
                <option value="BOOKS">Books</option>
                <option value="SPORTS">Sports</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          )}
          <div className="col-span-2">
            <input
              type="text"
              placeholder="Location found"
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="col-span-1">
            {/* Reward filter placeholder */}
          </div>
          <div className={user?.isAdmin ? "col-span-1" : "col-span-2"}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">A-Z</option>
            </select>
          </div>
          {user?.isAdmin && <div className="col-span-2" />}
        </div>
      </div>
    </div>
  );

  if (!user || !selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {!user ? "Authentication Required" : "No Location Selected"}
            </h2>
            <p className="text-gray-600">
              {!user 
                ? "You must be logged in to view lost & found items." 
                : "Please select a location to view lost & found items."}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <EnhancedPageLayout
        header={headerContent}
        filters={filtersContent}
      >
        <div className="w-full">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading items...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Package className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Error Loading Items
                </h3>
                <p className="text-gray-600">Please try again later.</p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredItems.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Items Found
                </h3>
                <p className="text-gray-600">
                  {searchTerm || selectedType !== "all"
                    ? "Try adjusting your search or filters to find what you're looking for."
                    : "No lost or found items have been reported in this location yet."}
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {!isLoading && !error && filteredItems.length > 0 && (
            <>
              {/* Cards View */}
              {viewMode === "cards" && (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredItems.map((item) => (
                      <div
                        key={item.id}
                        className={enhancedPageLayoutStyles.card.container}
                        onClick={() => navigate(`/lost-found/${item.id}`)}
                      >
                        <div className={enhancedPageLayoutStyles.card.content}>
                          <div className={enhancedPageLayoutStyles.card.header}>
                            <div className="flex items-center gap-2">
                              <span className={cn("px-2 py-0.5 rounded text-xs font-medium", getTypeColor(item.type))}>
                                {item.type === "LOST_ITEM" ? "Lost" : "Found"}
                              </span>
                              {showCategoryField && (
                                <span className={cn("px-2 py-0.5 rounded text-xs font-medium", getCategoryColor(item.metadata?.category))}>
                                  {item.metadata?.category}
                                </span>
                              )}
                            </div>
                            {showRewardField && item.metadata?.reward != null && (
                              <div className="text-lg font-bold text-green-600">
                                ${item.metadata.reward}
                              </div>
                            )}
                          </div>
                          <h3 className={enhancedPageLayoutStyles.card.title}>{item.title}</h3>
                          <p className={enhancedPageLayoutStyles.card.description}>{item.description}</p>
                          <div className="flex flex-col gap-2 mb-4">
                            {item.metadata?.locationFound && (
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <MapPin className="w-3.5 h-3.5" />
                                <span>{item.metadata.locationFound}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className={enhancedPageLayoutStyles.card.footer}>
                            <div className={enhancedPageLayoutStyles.card.meta}>
                              by {item.author.name || item.author.email}
                            </div>
                            <div className="flex items-center gap-2" onClick={(e) => e?.stopPropagation()}>
                              <EnhancedIconButton
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  navigate(`/lost-found/${item.id}`);
                                }}
                                icon={Eye}
                                title="View details"
                              />
                              {user?.isAdmin && (
                                <EnhancedIconButton
                                  onClick={(e) => {
                                    e?.stopPropagation();
                                    handleDelete(item.id);
                                  }}
                                  icon={Trash2}
                                  title="Delete"
                                  variant="danger"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* List View */}
              {viewMode === "list" && (
                <div className="p-6 space-y-4">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className={enhancedPageLayoutStyles.list.item}
                      onClick={() => navigate(`/lost-found/${item.id}`)}
                    >
                      <div className={enhancedPageLayoutStyles.list.content}>
                        <div className={enhancedPageLayoutStyles.list.left}>
                          <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", getTypeColor(item.type))}>
                            <Package className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {item.title}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={cn("px-2 py-0.5 rounded text-[10px] font-medium", getCategoryColor(item.metadata?.category))}>
                                {item.metadata?.category}
                              </span>
                              <span className="text-xs text-gray-500">
                                {item.author.name || item.author.email} • {new Date(item.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={enhancedPageLayoutStyles.list.right}>
                          {showRewardField && item.metadata?.reward != null && (
                            <div className="text-right mr-4">
                              <span className="block text-sm font-bold text-green-600">
                                ${item.metadata.reward}
                              </span>
                            </div>
                          )}
                          <EnhancedIconButton
                            onClick={(e) => {
                              e?.stopPropagation();
                              navigate(`/lost-found/${item.id}`);
                            }}
                            icon={Eye}
                            title="View"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Table View */}
              {viewMode === "table" && (
                <div className="w-full">
                  <div className={enhancedPageLayoutStyles.table.container}>
                    <div className={enhancedPageLayoutStyles.table.wrapper}>
                      <table className={enhancedPageLayoutStyles.table.table}>
                        <tbody className={enhancedPageLayoutStyles.table.tbody}>
                          {filteredItems.map((item) => (
                            <tr
                              key={item.id}
                              className="group hover:bg-sidebar-hover transition-colors cursor-pointer"
                              onClick={() => navigate(`/lost-found/${item.id}`)}
                            >
                              <td className={cn(enhancedPageLayoutStyles.table.td, showCategoryField ? "col-span-3" : "col-span-4")}>
                                <div className="px-6 py-4">
                                  <div className="font-medium text-gray-900 truncate">{item.title}</div>
                                  <div className="text-xs text-gray-500 truncate">{item.description}</div>
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-1")}>
                                <div className="px-6 py-4">
                                  <span className={cn("px-2 py-1 rounded-full text-[10px] font-medium", getTypeColor(item.type))}>
                                    {item.type === "LOST_ITEM" ? "Lost" : "Found"}
                                  </span>
                                </div>
                              </td>
                              {showCategoryField && (
                                <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-2")}>
                                  <div className="px-6 py-4 text-xs text-gray-600">
                                    {item.metadata?.category || "-"}
                                  </div>
                                </td>
                              )}
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-2")}>
                                <div className="px-6 py-4 text-sm text-gray-600 truncate">
                                  {item.metadata?.locationFound || "-"}
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-1")}>
                                <div className="px-6 py-4 text-sm font-bold text-green-600">
                                  {showRewardField && item.metadata?.reward != null ? `$${item.metadata.reward}` : "-"}
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, user?.isAdmin ? "col-span-1" : "col-span-2")}>
                                <div className="px-6 py-4 text-sm text-gray-600 truncate">
                                  {item.author.name || item.author.email}
                                </div>
                              </td>
                              {user?.isAdmin && (
                                <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-2")}>
                                  <div className="px-6 py-4 flex justify-end gap-2" onClick={(e) => e?.stopPropagation()}>
                                    <EnhancedIconButton
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        navigate(`/lost-found/${item.id}/edit`);
                                      }}
                                      icon={Edit}
                                      title="Edit"
                                    />
                                    <EnhancedIconButton
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        handleDelete(item.id);
                                      }}
                                      icon={Trash2}
                                      title="Delete"
                                      variant="danger"
                                    />
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </EnhancedPageLayout>
    </DashboardLayout>
  );
}
